import { forwardRef, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
	Dialog,
	DialogProps,
	DialogActions,
	DialogContent,
	DialogTitle,
	dialogTitleClasses,
	dialogContentClasses,
	dialogActionsClasses,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import styled from "styled-components";
import { enqueueSnackbar } from "notistack";

import Button from "../../styled/buttons/Button";
import EmptyHeader from "../../styled/EmptyHeader";
import EmptyMessage from "../../styled/EmptyMessage";
import SearchBar from "../../common/SearchBar";
import FXCheckboxItem, { StyledFXCheckboxItem } from "./FXCheckboxItem";

import { FXType } from "../../../types/fx.type";
import { MAX_FX_IN_WATCHLIST } from "../../../constants/watchlist.constant";
import { FX_SEARCH_IGNORE_SPECIAL_CHARACTER_REGEX } from "../../../constants/fx.constant";
import { boyerMooreTextSearch } from "../../../utils/common.utils";
import { useAppSelector } from "../../../utils/store.utils";
import { selectCurrencies, selectFXList } from "../../../slice/fx";

import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";

interface FXMultipleSelectorDialogProps extends DialogProps {
	selectedFXList: FXType[];
	onConfirm: (updatedFXList: FXType[]) => void;
	onClose: () => void;
}

const Transition = forwardRef(function Transition(
	props: TransitionProps & {
		children: React.ReactElement;
	},
	ref: React.Ref<unknown>
) {
	return <Slide direction="up" ref={ref} {...props} />;
});

const FXMultipleSelectorDialogHeaderStartContainer = styled.div`
	min-width: 24px;
	line-height: 1;
`;

const FXMultipleSelectorDialogHeaderTitle = styled.div`
	flex: 1 0 auto;
	margin-left: 16px;
	margin-right: 16px;
	font-weight: var(--font-weight-semi-bold);
	text-align: center;
`;

const FXMultipleSelectorDialogHeaderEndContainer = styled.div`
	min-width: 24px;
	line-height: 1;
`;

const FXMultipleSelectorDialogHeader = styled(DialogTitle)`
	display: flex;
	align-items: center;
	min-height: calc(44px + constant(safe-area-inset-top));
	min-height: calc(44px + env(safe-area-inset-top));
	padding-top: calc(18px + constant(safe-area-inset-top));
	padding-top: calc(18px + env(safe-area-inset-top));
	padding-right: 24px;
	padding-bottom: 18px;
	padding-left: 24px;
	background-color: var(--background-color-primary);
	user-select: none;
`;

const StyledFXMultipleSelectorDialog = styled(Dialog)`
	.${dialogTitleClasses.root} {
		display: flex;
		justify-content: space-between;
		min-height: calc(44px + constant(safe-area-inset-top));
		min-height: calc(44px + env(safe-area-inset-top));
		padding-top: calc(18px + constant(safe-area-inset-top));
		padding-top: calc(18px + env(safe-area-inset-top));
		padding-right: 24px;
		padding-bottom: 18px;
		padding-left: 24px;
		font-family: inherit;
		font-size: 16px;
		letter-spacing: normal;

		&.close-only {
			justify-content: flex-end;
		}
	}

	.${dialogContentClasses.root} {
		overflow-y: hidden;
	}
`;

const FXMultipleSelectorDialogContent = styled(DialogContent)`
	&.${dialogContentClasses.root} {
		display: flex;
		flex-direction: column;
		padding: 0;
		background-color: var(--background-color-primary);
	}
`;

const FXMultipleSelectorDialogActions = styled(DialogActions)`
	&.${dialogActionsClasses.root} {
		justify-content: space-between;
		border-top: 1px solid var(--border-color);
		padding: 16px 24px;
		font-size: 14px;
		font-weight: var(--font-weight-semi-bold);
		line-height: 18px;
	}
`;

const FXListContainer = styled.div`
	padding-top: 24px;
	overflow-y: auto;

	${StyledFXCheckboxItem}:last-child {
		border-bottom: 0;
	}
`;

const FXMultipleSelectorDialog = ({
	open,
	selectedFXList,
	onConfirm,
	onClose,
}: FXMultipleSelectorDialogProps) => {
	const intl = useIntl();
	const currencies = useAppSelector(selectCurrencies);
	const fxList = useAppSelector(selectFXList);
	const [filteredFXList, setFilteredFXList] = useState<FXType[]>(
		JSON.parse(JSON.stringify(fxList))
	);
	const [searchText, setSearchText] = useState<string>("");
	const [currentSelectedFXList, setCurrentSelectedFXList] = useState<FXType[]>(
		JSON.parse(JSON.stringify(selectedFXList))
	);

	useEffect(() => {
		setCurrentSelectedFXList(JSON.parse(JSON.stringify(selectedFXList)));
	}, [selectedFXList]);

	// Reset all state on exited
	const handleExited = () => {
		handleSearchTextClear();
		setFilteredFXList(JSON.parse(JSON.stringify(fxList)));
		setCurrentSelectedFXList(JSON.parse(JSON.stringify(selectedFXList)));
	};

	/**
	 * Handles the search functionality based on the given text.
	 *
	 * @param {string} text - The search text.
	 */
	const handleSearch = (text: string) => {
		if (text.length < 1) {
			setFilteredFXList(fxList);
		} else {
			let filteredCurrency: string[] = [];
			const splittedSearchText = text.split(" ").filter((v) => v && v !== "");
			baseLoop: for (let i = 0; i < currencies.length; i++) {
				for (let j = 0; j < splittedSearchText.length; j++) {
					if (
						boyerMooreTextSearch(
							currencies[i].currency.toLowerCase(),
							splittedSearchText[j].toLowerCase()
						) > -1
					) {
						filteredCurrency.push(currencies[i].currency);
						continue baseLoop;
					}
				}

				currencyKeywordLoop: for (
					let j = 0;
					j < currencies[i].keywords.length;
					j++
				) {
					for (let k = 0; k < splittedSearchText.length; k++) {
						const index = boyerMooreTextSearch(
							currencies[i].keywords[j].toLowerCase(),
							splittedSearchText[k].toLowerCase()
						);
						if (index > -1) {
							filteredCurrency.push(currencies[i].currency);
							break currencyKeywordLoop;
						}
					}
				}
			}

			setFilteredFXList(
				fxList.filter((v) => {
					const fxKeywords = [
						`${v.currencies[0]}${v.currencies[1]}`,
						`${v.currencies[1]}${v.currencies[0]}`,
					];

					for (let i = 0; i < fxKeywords.length; i++) {
						const formatted = text
							.toLowerCase()
							.replaceAll(FX_SEARCH_IGNORE_SPECIAL_CHARACTER_REGEX, "");
						if (
							formatted !== "" &&
							boyerMooreTextSearch(fxKeywords[i].toLowerCase(), formatted) > -1
						) {
							return v;
						}
					}

					for (let i = 0; i < v.currencies.length; i++) {
						if (filteredCurrency.indexOf(v.currencies[i]) > -1) return v;
					}
				})
			);
		}
	};

	const handleFXSelect = (fxObject: FXType, checked: boolean) => {
		let updatedCurrentSelectedFXList = [...currentSelectedFXList];
		if (checked) {
			if (currentSelectedFXList.length >= MAX_FX_IN_WATCHLIST) {
				enqueueSnackbar(
					intl.formatMessage(
						{
							id: "app.page.watchlistEdit.dialog.fxSelector.snackbar.max.message",
						},
						{ max: MAX_FX_IN_WATCHLIST }
					),
					{ variant: "general", mode: "info" }
				);
				return;
			}

			updatedCurrentSelectedFXList = [
				...updatedCurrentSelectedFXList,
				fxObject,
			];
		} else {
			const index = updatedCurrentSelectedFXList
				.map((v) => v.symbol)
				.indexOf(fxObject.symbol);
			if (index > -1) updatedCurrentSelectedFXList.splice(index, 1);
		}
		setCurrentSelectedFXList(updatedCurrentSelectedFXList);
	};

	const handleSearchBarChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setSearchText(event.currentTarget.value);
		handleSearch(event.currentTarget.value);
	};

	const handleSearchTextClear = () => {
		setSearchText("");
		handleSearch("");
	};

	const handleConfirm = () => {
		onConfirm(currentSelectedFXList);
		onClose();
	};

	return (
		<StyledFXMultipleSelectorDialog
			open={open}
			fullScreen
			TransitionComponent={Transition}
			TransitionProps={{ onExited: handleExited }}
			onClose={onClose}
		>
			<FXMultipleSelectorDialogHeader>
				<FXMultipleSelectorDialogHeaderStartContainer></FXMultipleSelectorDialogHeaderStartContainer>
				<FXMultipleSelectorDialogHeaderTitle>
					{intl.formatMessage({
						id: "app.page.watchlistEdit.dialog.fxSelector.title",
					})}
				</FXMultipleSelectorDialogHeaderTitle>
				<FXMultipleSelectorDialogHeaderEndContainer>
					<CloseIcon className="mbb-icon" onClick={onClose} />
				</FXMultipleSelectorDialogHeaderEndContainer>
			</FXMultipleSelectorDialogHeader>
			<FXMultipleSelectorDialogContent>
				<SearchBar
					value={searchText}
					onClear={handleSearchTextClear}
					inputProps={{
						onChange: handleSearchBarChange,
					}}
				/>
				<FXListContainer>
					{filteredFXList.length > 0 ? (
						filteredFXList.map((v) => (
							<FXCheckboxItem
								key={`fx-item-checkbox-${v.symbol}`}
								id={`fx-item-checkbox-${v.symbol}`}
								fxObject={v}
								checked={
									currentSelectedFXList.findIndex(
										(fx) => fx.symbol === v.symbol
									) > -1
								}
								onChange={handleFXSelect}
							/>
						))
					) : (
						<>
							<EmptyHeader>
								{intl.formatMessage({
									id: "app.page.watchlistEdit.dialog.fxSelector.message.empty.header",
								})}
							</EmptyHeader>
							<EmptyMessage>
								{intl.formatMessage({
									id: "app.page.watchlistEdit.dialog.fxSelector.message.empty.content",
								})}
							</EmptyMessage>
						</>
					)}
				</FXListContainer>
			</FXMultipleSelectorDialogContent>
			<FXMultipleSelectorDialogActions>
				{intl.formatMessage(
					{
						id: "app.page.watchlistEdit.dialog.fxSelector.message.count",
					},
					{ current: currentSelectedFXList.length, max: MAX_FX_IN_WATCHLIST }
				)}
				<Button
					theme="primary"
					onClick={handleConfirm}
					disabled={currentSelectedFXList.length < 1}
				>
					{intl.formatMessage({
						id: "app.page.watchlistEdit.dialog.fxSelector.button.ok",
					})}
				</Button>
			</FXMultipleSelectorDialogActions>
		</StyledFXMultipleSelectorDialog>
	);
};

export default FXMultipleSelectorDialog;
